import React, {
  FC,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import AccessRights from "../../components/AccessRights/AccessRights";
import Icon from "../../components/Icon/Icon";
import { Span } from "../../components/Text/Text";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  AccessLevelE,
  ClientTypeE,
  DirectionE,
  IconColorE,
  LangE,
  ProjectStatusE,
  UserRoleE,
} from "../../models/enums";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import DeliveryNumbersList from "./DeliveryNumbersList";
import {
  DATE_FORMAT,
  DEFAULT_ADVERTISER_AGENCY_ID,
  JUMBO_DELIVERY_NAME,
} from "../../utils/constants";
import {
  selectCurrentLang,
  selectDirection,
} from "../../store/selectors/appSelectors";
import { useTranslate } from "../../utils/hooks/useTranslate";
import showWaitingActionIconFor from "../../utils/conditions/showWaitingActionIconFor";
import useCampaignActions from "../../utils/hooks/useCampaignActions";
import BroadcasterLogo from "../projects/components/BroadcasterLogo/BroadcasterLogo";
import AdAgencyName from "./AdAgencyName";
import AdvertiserName from "./AdvertiserName";
import BrandName from "./BrandName";
import { isMobileOrTablet, isTablet } from "../../utils/helpers/mediaQueries";
import { Swiper, SwiperSlide } from "swiper/react";
import SwipeImage from "../../components/SwipeImage/SwipeImage";
import { useCopyText } from "../../utils/helpers/copyText";
import StatusTag from "../../components/StatusTag/StatusTag";
import ReDeliveryIndicator from "../../components/ReDeliveryIndicator/ReDeliveryIndicator";
import Portal from "../../components/Portal/Portal";

import styles from "./styles.module.scss";
import { VersionT } from "../../models/models";

type CampaignListItemPropTypes = {
  campaign: any;
  index: number;
  showContextMenuIndex: number;
  setShowContextMenuIndex: (index: any) => void;
  isLastElement: boolean;
  paginationElement: ReactElement;
  setSize: (index: number, size: number) => void;
  windowWidth: number;
};

const SWIPE_STORAGE_ITEM_NAME = "swipeCampaign";

/** Match .contextMenu offsets inside .campaignListItemMobile (top / inset-inline-end). */
const CONTEXT_MENU_TOP_OFFSET = 40;
const CONTEXT_MENU_INLINE_END_OFFSET = 20;

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  if (!node) {
    return null;
  }
  let el: HTMLElement | null = node.parentElement;
  while (el) {
    const { overflow, overflowY } = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(`${overflow}${overflowY}`)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

const CampaignListItem: FC<CampaignListItemPropTypes> = memo(
  (props) => {
    const {
      campaign,
      index,
      showContextMenuIndex,
      setShowContextMenuIndex,
      isLastElement,
      paginationElement,
      setSize,
      windowWidth,
    } = props;
    const navigate = useNavigate();
    const user = useAppSelector(selectCurrentUser);
    const direction = useAppSelector(selectDirection);
    const lang = useAppSelector(selectCurrentLang);
    const { t } = useTranslate();
    const copyText = useCopyText();
    const campaignLink = useMemo(
      () => `/campaigns/${campaign.CampaignId}`,
      [campaign.CampaignId]
    );
    const deliveryStatuses = useMemo(
      () =>
        campaign?.projectIds?.length
          ? campaign.projectIds?.map(({ Status }) => Status)
          : [],
      [campaign]
    );
    const deliveries = useMemo(
      () =>
        campaign?.projectIds?.filter(({ Status }) => {
          return (
            Status !== ProjectStatusE.Metadata &&
            (user.role !== UserRoleE.AdvertisingAgency &&
            +user.accessLevel !== +AccessLevelE.GlobalAdmin
              ? Status !== ProjectStatusE.Creating
              : true)
          );
        }) || [],
      [campaign?.projectIds]
    );
    const [isSwipeShown, setIsSwipeShown] = useState<boolean>(
      !!localStorage.getItem(SWIPE_STORAGE_ITEM_NAME)
    );
    const [contextMenuStyle, setContextMenuStyle] =
      useState<React.CSSProperties | null>(null);
    const liRef = useRef<HTMLLIElement>();
    const contextMenuPanelRef = useRef<HTMLUListElement>(null);
    const contextMenuTriggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (liRef.current) {
        setSize(index, liRef.current.getBoundingClientRect().height);
      }
    }, [setSize, index, liRef.current, windowWidth]);

    const { defaultActions, hasAvailableActions, handleCreateDelivery } = useCampaignActions(
      +campaign.CampaignId,
      campaign?.campaing_metadata?.CreatedByUser,
      deliveries?.length,
      campaign.projectIds[0]?.Id,
      campaign.projectIds[campaign.projectIds?.length - 1]?.Id,
      deliveryStatuses
    );

    const toggleShowContextMenu = () => {
      setShowContextMenuIndex((prev) => (prev === index ? -1 : index));
    };

    const navigateToCampaign = (e) => {
      e.preventDefault();
      navigate(campaignLink);
    };

    const showWaitingActionIcon = useMemo(
      () =>
        showWaitingActionIconFor(user.role, user.clientType, deliveryStatuses),
      [deliveryStatuses, user.clientType]
    );
    const isActionShown =
      user.role === UserRoleE.GlobalAdmin ||
      user.role === UserRoleE.GlobalAdminAndAdminContentManager ||
      user.role === UserRoleE.AdvertisingAgency ||
      (campaign?.campaing_metadata?.CreatedByUser.includes(JUMBO_DELIVERY_NAME)
        ? true
        : user.userName === campaign?.campaing_metadata?.CreatedByUser);

    const closeContextMenu = useCallback(() => {
      setShowContextMenuIndex(-1);
    }, [setShowContextMenuIndex]);

    const canShowMobileContextMenu =
      hasAvailableActions ||
      (user?.role === UserRoleE.AdvertisingAgency && isActionShown);
    const isMobileContextMenuOpen =
      isMobileOrTablet() &&
      showContextMenuIndex === index &&
      canShowMobileContextMenu;

    useLayoutEffect(() => {
      if (!isMobileContextMenuOpen || !liRef.current) {
        setContextMenuStyle(null);
        return;
      }
      const el = liRef.current;
      const updatePosition = () => {
        const rect = el.getBoundingClientRect();
        const top = rect.top + CONTEXT_MENU_TOP_OFFSET;
        if (direction === DirectionE.RTL) {
          setContextMenuStyle({
            position: "fixed",
            top,
            left: rect.left + CONTEXT_MENU_INLINE_END_OFFSET,
            zIndex: 10001,
          });
        } else {
          setContextMenuStyle({
            position: "fixed",
            top,
            right:
              window.innerWidth -
              rect.right +
              CONTEXT_MENU_INLINE_END_OFFSET,
            zIndex: 10001,
          });
        }
      };
      updatePosition();
      const scrollParent = getScrollParent(el);
      const onListScroll = () => closeContextMenu();
      window.addEventListener("resize", updatePosition);
      scrollParent?.addEventListener("scroll", onListScroll, {
        passive: true,
      });
      return () => {
        window.removeEventListener("resize", updatePosition);
        scrollParent?.removeEventListener("scroll", onListScroll);
      };
    }, [
      isMobileContextMenuOpen,
      direction,
      windowWidth,
      index,
      closeContextMenu,
    ]);

    useEffect(() => {
      if (!isMobileContextMenuOpen || !contextMenuStyle) {
        return;
      }
      const onPointerDown = (e: PointerEvent) => {
        const target = e.target as Node;
        if (contextMenuPanelRef.current?.contains(target)) {
          return;
        }
        if (contextMenuTriggerRef.current?.contains(target)) {
          return;
        }
        closeContextMenu();
      };
      document.addEventListener("pointerdown", onPointerDown, true);
      return () => {
        document.removeEventListener("pointerdown", onPointerDown, true);
      };
    }, [isMobileContextMenuOpen, contextMenuStyle, closeContextMenu]);

    const handleSwipeChange = () => {
      if (!isSwipeShown) {
        localStorage.setItem(SWIPE_STORAGE_ITEM_NAME, "true");
        setIsSwipeShown(true);
      }
    };

    // add pagination to the footer of the list
    if (isMobileOrTablet() && isLastElement)
      return <li ref={liRef}>{paginationElement}</li>;

    return isMobileOrTablet() ? (
      <li
        key={campaign.CampaignId}
        className={styles.campaignListItemMobile}
        dir={direction}
        ref={liRef}
      >
        <div>
          <Tooltip text={t`Go to campaign page`}>
            <span
              onClick={navigateToCampaign}
              className={styles.mobileCampaignLink}
            >
              <span>
                <strong>{campaign.CampaignId}</strong> {campaign.CampaignName}
              </span>
            </span>
          </Tooltip>
          {(hasAvailableActions ||
            (user?.role === UserRoleE.AdvertisingAgency && isActionShown)) && (
            <div
              className={styles.iconsMobile}
              ref={contextMenuTriggerRef}
            >
              <Icon name="menu" onClick={toggleShowContextMenu} />
            </div>
          )}
        </div>
        <Swiper
          onSlideChange={handleSwipeChange}
          slidesPerView={isTablet() ? 2 : 1}
        >
          <SwiperSlide>
            {index === 0 && !isSwipeShown && <SwipeImage />}
            <ul className={styles.campaignInfoMobile}>
              <li>
                <AdvertiserName campaign={campaign?.campaing_metadata} />
              </li>
              <li>
                <BrandName campaign={campaign?.campaing_metadata} />
              </li>
              <li>
                <AdAgencyName campaign={campaign?.campaing_metadata} />
              </li>
              <li>
                <span>
                  <Span>Broadcasting date</Span>:&nbsp;
                </span>
                <span>
                  {dayjs(campaign?.campaing_metadata?.BroadcastingDate).format(
                    DATE_FORMAT
                  )}
                </span>
              </li>
            </ul>
          </SwiperSlide>
          <SwiperSlide>
            <ul className={styles.campaignInfoMobile}>
              {!!deliveries?.length && (
                <li>
                  <span>
                    <Span>Post-Production</Span>:&nbsp;
                  </span>
                  <span>
                    {campaign?.campaing_metadata?.PostProductionCompany}
                  </span>
                </li>
              )}
              <li>
                <span>
                  <Span>Production Company</Span>:&nbsp;
                </span>
                <span>
                  {campaign?.campaing_metadata?.ProductionCompanyName ||
                    t`NONE`}
                </span>
              </li>
              <li>
                <span>
                  <Span>Market category</Span>:&nbsp;
                </span>
                <span>
                  {
                    campaign?.campaing_metadata?.[
                      lang === LangE.HE ? "MarketCategory" : "MarketCategoryEN"
                    ]
                  }
                </span>
              </li>
              <li>
                <span>
                  <Span>Creation date</Span>:&nbsp;
                </span>
                <span>
                  {dayjs(campaign?.campaing_metadata?.CreationDate).format(
                    DATE_FORMAT
                  )}
                </span>
              </li>
            </ul>
          </SwiperSlide>
        </Swiper>
        <div className={styles.deliveriesListMobileWrapper}>
          <DeliveryNumbersList deliveries={deliveries} />
        </div>
        {isMobileContextMenuOpen && contextMenuStyle && (
          <Portal id="root">
            <ul
              ref={contextMenuPanelRef}
              className={styles.contextMenu}
              style={contextMenuStyle}
              dir={direction}
              role="menu"
            >
              <AccessRights
                accessFor={[UserRoleE.AdvertisingAgency]}
                condition={isActionShown}
              >
                <li
                  onClick={() => {
                    handleCreateDelivery();
                    closeContextMenu();
                  }}
                >
                  <Icon name="add" title="Create delivery" />
                  <Span>Create delivery</Span>
                </li>
              </AccessRights>
              {defaultActions
                .filter(({ icon }) => icon !== "copy")
                .map(({ name, icon, action, accessFor, condition }) => (
                  <AccessRights
                    accessFor={accessFor}
                    condition={condition}
                    key={name}
                  >
                    <li
                      onClick={() => {
                        action();
                        closeContextMenu();
                      }}
                    >
                      <Icon name={icon} title={name} />
                      <Span>{name}</Span>
                    </li>
                  </AccessRights>
                ))}
            </ul>
          </Portal>
        )}
      </li>
    ) : (
      <li
        key={campaign.CampaignId}
        className={styles.campaignListItem}
        dir={direction}
        ref={liRef}
      >
        <div className={styles.header}>
          <div className={styles.campaignNameWrapper}>
            <Icon
              name="copy"
              color={IconColorE.BLUE}
              className={styles.copyNumberIcon}
              onClick={() => copyText(campaign.CampaignId)}
            />
            <Tooltip text="Go to campaign page">
              <a href={campaignLink} onClick={navigateToCampaign}>
                <span>{campaign.CampaignId}</span>
                <span>{campaign.CampaignName}</span>
              </a>
            </Tooltip>
            <ReDeliveryIndicator
              isReDelivery={campaign?.campaing_metadata?.IsReDelivery}
              sourceNumber={campaign?.campaing_metadata?.SourceCampaignId}
              href={
                campaign?.campaing_metadata?.SourceCampaignId
                  ? `/campaigns/${campaign.campaing_metadata.SourceCampaignId}`
                  : null
              }
              type="campaign"
            />
            {campaign?.isDeletedCampaign && (
              <div className={styles.deletedStatusWrapper}>
                <StatusTag status="Deleted" isRemovable={false} />
              </div>
            )}
          </div>
          <div className={styles.createByUser}>
            <AccessRights
              accessFor={[
                UserRoleE.GlobalAdmin,
                UserRoleE.GlobalAdminAndAdminContentManager,
                UserRoleE.AdvertisingAgency,
              ]}
              condition={
                campaign?.campaing_metadata?.CreatedByUserType ===
                  ClientTypeE.Broadcaster &&
                (user.role === UserRoleE.AdvertisingAgency
                  ? +user.id === DEFAULT_ADVERTISER_AGENCY_ID
                  : true)
              }
            >
              <Span className={styles.innerCampaignWrapper}>
                Inner campaign
              </Span>
            </AccessRights>
            {/* dollar sign should be displayed for each delivery and not the campaign*/}
            {/* <DollarSign condition={campaign?.campaing_metadata?.CreatedByUserType === ClientTypeE['Delivery service']} /> */}
          </div>
          <DeliveryNumbersList deliveries={deliveries} />
        </div>
        <ul className={styles.campaignInfo}>
          <li>
            <AdvertiserName campaign={campaign?.campaing_metadata} />
          </li>
          <li>
            <BrandName campaign={campaign?.campaing_metadata} />
          </li>
          <li>
            <AdAgencyName campaign={campaign?.campaing_metadata} />
          </li>
          <li>
            <span>
              <Span>Broadcasting date</Span>:&nbsp;
            </span>
            <span>
              {dayjs(campaign?.campaing_metadata?.BroadcastingDate).format(
                DATE_FORMAT
              )}
            </span>
          </li>
          {!!deliveries?.length && (
            <li>
              <span>
                <Span>Post-Production</Span>:&nbsp;
              </span>
              <span>{campaign?.campaing_metadata?.PostProductionCompany}</span>
            </li>
          )}
          <li>
            <span>
              <Span>Production Company</Span>:&nbsp;
            </span>
            <span>
              {campaign?.campaing_metadata?.ProductionCompanyName || t`NONE`}
            </span>
          </li>
          <li>
            <span>
              <Span>Market category</Span>:&nbsp;
            </span>
            <span>
              {
                campaign?.campaing_metadata?.[
                  lang === LangE.HE ? "MarketCategory" : "MarketCategoryEN"
                ]
              }
            </span>
          </li>
          <li>
            <span>
              <Span>Creation date</Span>:&nbsp;
            </span>
            <span>
              {dayjs(campaign?.campaing_metadata?.CreationDate).format(
                DATE_FORMAT
              )}
            </span>
          </li>
        </ul>
        <div className={styles.itemFooter}>
          <ul>
            {campaign?.campaing_metadata?.BroadcastersLogoInfo?.map(
              ({ BroadcasterId, BroadcasterName }) => (
                <li key={BroadcasterId}>
                  <BroadcasterLogo id={BroadcasterId} name={BroadcasterName} />
                </li>
              )
            )}
          </ul>
          <div className={styles.icons}>
            {showWaitingActionIcon && (
              <Icon
                name="timer"
                color={IconColorE.ORANGE}
                className={styles.timer}
                title="Waiting for action"
              />
            )}
            <ul>
              {campaign?.projectIds?.some((delivery) =>
                delivery?.ProjectFiles?.some(
                  (version: VersionT) => version.NotSuitableForBroadcast
                )
              ) ? (
                <li>
                  <Icon
                    name="exclamation"
                    title="Some of the versions are not suitable for broadcasting"
                    tooltipClassName={styles.exclamationTooltip}
                  />
                </li>
              ) : null}
              <li>
                <AccessRights
                  accessFor={[UserRoleE.AdvertisingAgency]}
                  condition={isActionShown}
                >
                  <div id={index === 0 ? "create-delivery-button" : undefined}>
                    <Icon
                      name="add"
                      title="Create delivery"
                      onClick={handleCreateDelivery}
                    />
                  </div>
                </AccessRights>
              </li>
              {defaultActions
                .filter(({ icon }) => icon !== "copy")
                .map(({ name, icon, action, accessFor, condition }) => (
                  <li key={name}>
                    <AccessRights accessFor={accessFor} condition={condition}>
                      <Icon name={icon} title={name} onClick={action} />
                    </AccessRights>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </li>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.showContextMenuIndex === nextProps.showContextMenuIndex &&
      prevProps.index === nextProps.index &&
      prevProps.windowWidth === nextProps.windowWidth &&
      prevProps.isLastElement === nextProps.isLastElement &&
      prevProps.paginationElement === nextProps.paginationElement &&
      prevProps.campaign.CampaignId === nextProps.campaign.CampaignId &&
      prevProps.campaign.projectIds.length ===
        nextProps.campaign.projectIds.length &&
      prevProps.campaign.campaignName === nextProps.campaign.campaignName &&
      prevProps.campaign.advertiserName === nextProps.campaign.advertiserName &&
      prevProps.campaign.brandName === nextProps.campaign.brandName &&
      prevProps.campaign.advertisingAgencyName ===
        nextProps.campaign.advertisingAgencyName &&
      prevProps.campaign.broadcastingDate ===
        nextProps.campaign.broadcastingDate &&
      prevProps.campaign.industry === nextProps.campaign.industry &&
      prevProps.campaign.postProductionCompanyName ===
        nextProps.campaign.postProductionCompanyName &&
      prevProps.campaign.creationDateTime ===
        nextProps.campaign.creationDateTime
    );
  }
);

export default CampaignListItem;
